import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ListSubheader from '@mui/material/ListSubheader';

import { useActiveClass } from 'src/class/class-provider';

// ----------------------------------------------------------------------

// "ACTIVE" → "Current year". Shown as a per-year caption ('' → no caption).
function statusLabel(status: string | null): string {
  if (status === 'ACTIVE') return 'Current year';
  if (status === 'ARCHIVED') return 'Past year';
  if (status === 'DRAFT') return 'Draft';
  return '';
}

type Props = { anchorEl: HTMLElement | null; onClose: () => void };

/**
 * Every (school, year) the teacher can act in, grouped by school when there is more than one.
 * Opened from the class menu footer, the header's school / year chip and the account drawer —
 * the header has no school picker of its own (epic #617). Picking a year resets the class to
 * that year's first class (`setActiveSchoolYearId`).
 */
export function SchoolYearMenu({ anchorEl, onClose }: Props) {
  const { schoolYearOptions, activeSchoolYearId, setActiveSchoolYearId } = useActiveClass();
  const schools = [...new Set(schoolYearOptions.map((o) => o.schoolName))];
  const grouped = schools.length > 1;

  return (
    <Menu
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      slotProps={{ paper: { sx: { minWidth: 260 } } }}
    >
      {schools.flatMap((school) => [
        ...(grouped ? [<ListSubheader key={`h-${school}`}>{school}</ListSubheader>] : []),
        ...schoolYearOptions
          .filter((o) => o.schoolName === school)
          .map((o) => (
            <MenuItem
              key={o.yearId}
              selected={o.yearId === activeSchoolYearId}
              onClick={() => {
                onClose();
                if (o.yearId !== activeSchoolYearId) setActiveSchoolYearId(o.yearId);
              }}
            >
              {grouped ? o.yearName : `${o.schoolName} · ${o.yearName}`}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 2 }}>
                {statusLabel(o.status)}
              </Typography>
            </MenuItem>
          )),
      ])}
    </Menu>
  );
}
